import { Constructor } from '@angular/cdk/table';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { IVideoMediaDto } from 'src/app/core/dtos/interfaces/i-video-media-dto';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { compareArrays } from '../utils/array-utils';

type WithMainVideoImageFormSectionType = {
  _initMainVideoImage(
    mainImage: IImgMediaDto,
    mainVideo: IVideoMediaDto,
    displayImageOnWebsite: boolean
  ): void;
  _initMainImageVideoValueChanges(
    getPreviousMainImageValue: () => IImgMediaDto | null,
    getPreviousMainVideoValue: () => IVideoMediaDto | null,
    getPreviousDisplayImageOnWebsiteValue: () => boolean
  ): void;
  _buildMainImage(): IImgMediaDto;
  _buildMainVideo(): IVideoMediaDto;
  _buildDisplayImageOnWebsite(): boolean;
};

type MainVideoImageFormSectionRequerimentsBase = {
  form: FormGroup;
  FORM_PATHS: {
    MAIN_IMAGE: () => string;
    MAIN_VIDEO: () => string;
    DISPLAY_IMAGE_ON_WEBSITE: () => string;
  };
  destroy$: Subject<boolean>;
};

export function WithMainVideoImageFormSection<
  TBase extends Constructor<MainVideoImageFormSectionRequerimentsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithMainVideoImageFormSectionType;
  prototype: WithMainVideoImageFormSectionType;
} & TBase {
  return class WithMainVideoImageFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initMainVideoImage(
      mainImage: IImgMediaDto,
      mainVideo: IVideoMediaDto,
      displayImageOnWebsite: boolean
    ): void {
      let mainImageControl = this.form.get(
        this.FORM_PATHS.MAIN_IMAGE()
      ) as FormArray<FormControl<IImgMediaDto>>;
      let mainVideoControl = this.form.get(
        this.FORM_PATHS.MAIN_VIDEO()
      ) as FormArray<FormControl<IVideoMediaDto>>;
      if (mainImage) mainImageControl.push(new FormControl(mainImage));
      if (mainVideo) mainVideoControl.push(new FormControl(mainVideo));
      this.form
        .get(this.FORM_PATHS.DISPLAY_IMAGE_ON_WEBSITE())
        .setValue(displayImageOnWebsite);
    }

    _initMainImageVideoValueChanges(
      getPreviousMainImageValue: () => IImgMediaDto | null,
      getPreviousMainVideoValue: () => IVideoMediaDto | null,
      getPreviousDisplayImageOnWebsiteValue: () => boolean
    ): void {
      if (getPreviousMainImageValue) {
        this.form
          .get(this.FORM_PATHS.MAIN_IMAGE())
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (newValue) => {
              if (compareArrays(newValue, [getPreviousMainImageValue()])) {
                this.form.get(this.FORM_PATHS.MAIN_IMAGE()).markAsPristine();
              } else {
                this.form.get(this.FORM_PATHS.MAIN_IMAGE()).markAsDirty();
              }
            },
          });
      }

      if (getPreviousMainVideoValue) {
        this.form
          .get(this.FORM_PATHS.MAIN_VIDEO())
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (newValue) => {
              if (compareArrays(newValue, [getPreviousMainVideoValue()])) {
                this.form.get(this.FORM_PATHS.MAIN_VIDEO()).markAsPristine();
              } else {
                this.form.get(this.FORM_PATHS.MAIN_VIDEO()).markAsDirty();
              }
            },
          });
      }

      this.form
        .get(this.FORM_PATHS.DISPLAY_IMAGE_ON_WEBSITE())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousDisplayImageOnWebsiteValue()) {
              this.form
                .get(this.FORM_PATHS.DISPLAY_IMAGE_ON_WEBSITE())
                .markAsPristine();
            }
          },
        });
    }

    _buildMainImage(): IImgMediaDto {
      return this.form.get(this.FORM_PATHS.MAIN_IMAGE()).value
        ? this.form.get(this.FORM_PATHS.MAIN_IMAGE()).value[0]
        : null;
    }

    _buildMainVideo(): IVideoMediaDto {
      return this.form.get(this.FORM_PATHS.MAIN_VIDEO()).value
        ? this.form.get(this.FORM_PATHS.MAIN_VIDEO()).value[0]
        : null;
    }
    _buildDisplayImageOnWebsite(): boolean {
      return this.form.get(this.FORM_PATHS.DISPLAY_IMAGE_ON_WEBSITE()).value;
    }
  };
}
